import * as React from "react"
import * as SeparatorPrimitive from "@radix-ui/react-separator"

import { cn } from "@/lib/utils"

/**
 * Separator component displays a visual divider line, either horizontal or vertical.
 * Built on Radix UI Separator primitive. Can be decorative or semantic.
 * 
 * @component
 * @param {React.ComponentPropsWithoutRef<typeof SeparatorPrimitive.Root>} props - Separator props
 * @param {"horizontal" | "vertical"} [props.orientation="horizontal"] - Orientation of the separator
 * @param {boolean} [props.decorative=true] - Whether the separator is decorative (not semantic)
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element} A separator line element
 * 
 * @example
 * ```tsx
 * <Separator orientation="horizontal" />
 * ```
 * 
 * @example
 * ```tsx
 * <Separator orientation="vertical" decorative={false} />
 * ```
 */
const Separator = React.forwardRef<
  React.ElementRef<typeof SeparatorPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof SeparatorPrimitive.Root>
>(
  (
    { className, orientation = "horizontal", decorative = true, ...props },
    ref
  ) => (
    <SeparatorPrimitive.Root
      ref={ref}
      decorative={decorative}
      orientation={orientation}
      className={cn(
        "shrink-0 bg-border",
        orientation === "horizontal" ? "h-[1px] w-full" : "h-full w-[1px]",
        className
      )}
      {...props}
    />
  )
)
Separator.displayName = SeparatorPrimitive.Root.displayName

export { Separator }
